<template>
    <div ref="dropdownRef" class="relative inline-block w-full">
        <div
            ref="trigger"
            class="flex w-full cursor-pointer items-center justify-between rounded-md border border-gray-300 dark:border-default bg-white dark:bg-surface-card"
            :class="[compact ? 'gap-1 px-1.5 py-1 text-[12px] leading-3-figma' : 'gap-2 p-2.5']"
            @click="toggle"
        >
            <slot name="elementBefore" />

            <span v-if="!normalizedValues.length" class="text-gray-500 dark:text-txt-body">{{ placeholder }}</span>
            <span v-else class="dark:text-txt-body">{{ normalizedValues.join(', ') }}</span>

            <slot name="elementAfter" />
        </div>

        <CTransitionSlideDown>
            <div
                v-if="isOpen && !disabled"
                class="absolute z-10 mt-1 w-full rounded-md bg-white dark:bg-surface-card shadow-dropdown"
                :class="{
                    'min-w-52': compact,
                    'p-3': !paddingNone,
                    'w-full bg-gray-100 dark:bg-subtle': filterWrapper
                }"
            >
                <CClose v-if="!filter && !filterWrapper" class="hidden" @close="close" />

                <div class="max-h-60 overflow-auto">
                    <label
                        v-for="option in Object.values(normalizedOptions)"
                        :key="option.value"
                        class="flex cursor-pointer select-none items-center gap-2 px-4 py-2 hover:bg-gray-200 dark:hover:bg-subtle dark:text-txt-body"
                    >
                        <input
                            :checked="modelValue.includes(option.value)"
                            type="checkbox"
                            :value="option.value"
                            @change="updateSelected(option.value)"
                        />
                        {{ option.label }}
                    </label>
                </div>
            </div>
        </CTransitionSlideDown>
    </div>
</template>

<script setup lang="ts">
import CClose from '@/components/ui/CClose.vue'
import CTransitionSlideDown from '@/components/ui/transitions/CTransitionSlideDown.vue'
import type { Extractor, Option } from '@/utils/normalizeOption'
import { normalizeOption, retrieveValue } from '@/utils/normalizeOption'
import { onClickOutside } from '@vueuse/core'
import type { PropType } from 'vue'
import { computed, ref } from 'vue'

const props = defineProps({
    modelValue: {
        type: Array as PropType<Option['value'][]>,
        default: () => []
    },
    options: {
        type: Array as PropType<Option['value'][] | Record<PropertyKey, Option['value']>[]>,
        default: () => []
    },
    labelKey: {
        type: [Function, String, Number] as PropType<Extractor>,
        default: undefined
    },
    valueKey: {
        type: [Function, String, Number] as PropType<Extractor>,
        default: undefined
    },
    disabledKey: {
        type: [Function, String, Number] as PropType<Extractor>,
        default: undefined
    },
    placeholder: {
        type: String,
        default: 'Select options'
    },
    filter: {
        type: Boolean,
        default: false
    },
    filterWrapper: {
        type: Boolean,
        default: false
    },
    paddingNone: {
        type: Boolean,
        default: false
    },
    compact: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const dropdownRef = ref(null)

// Setup click outside detection
onClickOutside(dropdownRef, close)

const currentValue = computed((): Option['value'][] => {
    return Array.isArray(props.modelValue) ? props.modelValue : []
})

const normalizedOptions = computed((): Record<Option['value'], Option> => {
    const options = Array.isArray(props.options) ? props.options : []

    return options.reduce(
        (acc, curr) => {
            const value = retrieveValue(curr, props.valueKey)

            acc[value] = normalizeOption(curr, {
                value: props.valueKey,
                label: props.labelKey,
                disabled: props.disabledKey
            })

            return acc
        },
        {} as Record<Option['value'], Option>
    )
})

const normalizedValues = computed((): Option['label'][] => {
    if (!currentValue.value.length) return []

    return currentValue.value.map((value) => {
        const option = normalizedOptions.value[value]
        return option ? option.label : value.toString()
    })
})

function toggle() {
    if (props.disabled) return
    if (props.filter || props.filterWrapper) return
    isOpen.value = !isOpen.value
}

function close() {
    isOpen.value = false
}

function updateSelected(value: Option['value']) {
    const valuesSet = new Set(currentValue.value)
    if (valuesSet.has(value)) valuesSet.delete(value)
    else valuesSet.add(value)
    emit('update:modelValue', Array.from(valuesSet))
}
</script>
